import { useState, useEffect, useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import { Users, ShoppingBag, Store, DollarSign } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

/**
 * LiveMetricsStrip - Animated impact metrics for the homepage
 * 
 * Displays ONLY real, verified transaction data from the backend.
 * If no real transactions exist, shows zeros (honest metrics).
 * 
 * Metrics shown:
 * - Families Helped: Unique sender-beneficiary pairs
 * - Purchases Completed: Real completed transactions
 * - Colmados Participating: Active approved merchants
 * - Total Authorized: Sum of USD from real transactions
 */
export function LiveMetricsStrip() {
  const [metrics, setMetrics] = useState({
    families_helped: 0,
    purchases_completed: 0,
    colmados_participating: 0,
    total_authorized_usd: 0,
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-50px" });

  useEffect(() => {
    const fetchMetrics = async () => {
      try {
        const response = await fetch(`${API_URL}/api/metrics/live`);
        if (response.ok) {
          const data = await response.json();
          setMetrics({
            families_helped: data.families_helped || 0,
            purchases_completed: data.purchases_completed || 0,
            colmados_participating: data.colmados_participating || 0,
            total_authorized_usd: data.total_authorized_usd || 0,
          });
        }
      } catch (err) {
        console.error('Failed to fetch live metrics:', err);
        setError(true);
      } finally {
        setLoading(false);
      }
    };

    fetchMetrics();
    
    // Refresh every 60 seconds
    const interval = setInterval(fetchMetrics, 60000);
    return () => clearInterval(interval);
  }, []);

  // Don't show the strip if there are no real transactions yet
  const hasRealData = metrics.families_helped > 0 || 
                      metrics.purchases_completed > 0 || 
                      metrics.colmados_participating > 0;

  // If loading or no data, don't render the strip at all
  if (loading || error || !hasRealData) {
    return null;
  }

  const metricItems = [
    {
      icon: Users,
      value: metrics.families_helped,
      label: 'Families Helped',
      labelEs: 'Familias Ayudadas',
      color: 'text-emerald-400',
      bgColor: 'bg-emerald-500/20',
    },
    {
      icon: ShoppingBag,
      value: metrics.purchases_completed,
      label: 'Purchases Completed',
      labelEs: 'Compras Completadas',
      color: 'text-blue-400',
      bgColor: 'bg-blue-500/20',
    },
    {
      icon: Store,
      value: metrics.colmados_participating,
      label: 'Colmados Participating',
      labelEs: 'Colmados Participantes',
      color: 'text-amber-400',
      bgColor: 'bg-amber-500/20',
    },
    {
      icon: DollarSign,
      value: metrics.total_authorized_usd,
      label: 'Total Authorized',
      labelEs: 'Total Autorizado',
      color: 'text-purple-400',
      bgColor: 'bg-purple-500/20',
      isCurrency: true,
    },
  ];

  return (
    <section 
      ref={ref}
      className="relative py-8 sm:py-10 bg-gradient-to-r from-slate-900 via-slate-800 to-slate-900 border-y border-slate-700/50"
      data-testid="live-metrics-strip"
    >
      {/* Subtle background pattern */}
      <div className="absolute inset-0 opacity-5">
        <div className="absolute inset-0" style={{
          backgroundImage: 'radial-gradient(circle at 1px 1px, white 1px, transparent 0)',
          backgroundSize: '24px 24px'
        }} />
      </div>
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12 relative">
        {/* Section title - mobile only */}
        <motion.p 
          initial={{ opacity: 0, y: -10 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.5 }}
          className="text-center text-xs text-emerald-400 uppercase tracking-wider mb-4 sm:hidden"
        >
          Real Impact
        </motion.p>
        
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6 lg:gap-8">
          {metricItems.map((item, index) => (
            <motion.div
              key={item.label}
              initial={{ opacity: 0, y: 20 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.5, delay: index * 0.1 }}
              className="relative group"
            >
              <div className="flex flex-col items-center text-center p-3 sm:p-4 rounded-xl hover:bg-white/5 transition-colors">
                {/* Icon */}
                <div className={`w-10 h-10 sm:w-12 sm:h-12 rounded-full ${item.bgColor} flex items-center justify-center mb-2 sm:mb-3 group-hover:scale-110 transition-transform`}>
                  <item.icon className={`w-5 h-5 sm:w-6 sm:h-6 ${item.color}`} />
                </div>
                
                {/* Animated Counter */}
                <AnimatedCounter 
                  value={item.value} 
                  isCurrency={item.isCurrency}
                  isInView={isInView}
                />
                
                {/* Label */}
                <p className="text-xs sm:text-sm text-slate-400 mt-1">
                  {item.label}
                </p>
              </div>
            </motion.div>
          ))}
        </div>
        
        {/* Disclaimer */}
        <motion.p 
          initial={{ opacity: 0 }}
          animate={isInView ? { opacity: 1 } : {}}
          transition={{ duration: 0.5, delay: 0.5 }}
          className="text-center text-[10px] text-slate-500 mt-4 sm:mt-6"
        >
          Live metrics from verified transactions only
        </motion.p>
      </div>
    </section>
  );
}

/**
 * AnimatedCounter - Counts up from 0 to target value
 */
function AnimatedCounter({ value, isCurrency = false, isInView }) {
  const [displayValue, setDisplayValue] = useState(0);
  const duration = 1500; // ms
  
  useEffect(() => {
    if (!isInView) return;
    
    let startTime = null;
    const startValue = 0;
    
    const animate = (timestamp) => {
      if (!startTime) startTime = timestamp;
      const progress = Math.min((timestamp - startTime) / duration, 1);
      
      // Ease out cubic
      const easeOut = 1 - Math.pow(1 - progress, 3);
      const currentValue = startValue + (value - startValue) * easeOut;
      
      setDisplayValue(currentValue);
      
      if (progress < 1) {
        requestAnimationFrame(animate);
      }
    };
    
    requestAnimationFrame(animate);
  }, [value, isInView]);
  
  const formattedValue = isCurrency
    ? `$${Math.round(displayValue).toLocaleString()}`
    : Math.round(displayValue).toLocaleString();
  
  return (
    <span className="text-2xl sm:text-3xl lg:text-4xl font-bold text-white tabular-nums">
      {formattedValue}
    </span>
  );
}

export default LiveMetricsStrip;
